import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  type CalculateMonthlyContributionParams,
  calculateMonthlyContribution,
  calculateMonthlyContributionWithFee,
  calculateMonthlyContributionWithoutFee,
  calculateTargetAmount,
} from "Charts/Utils/CommonChartUtils";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { ConfirmRecalculateTargetModal } from "Components/_Shared/Dialogs/ConfirmRecalculateTargetModal";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { DatePicker } from "Forms/Components/DatePicker";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { FormSwitch } from "Forms/Components/FormSwitch";
import { type VehicleGoalFormType, getVehicleGoalFormSchema, minDate } from "Forms/Schemas/Goals/vehicleGoalFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetVehicleGoalQueryKey,
  useCreateVehicleGoal,
  useUpdateVehicleGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type VehicleGoalDto, VehicleGoalDtoPriorityLevel } from "Infrastructure/ApiZfp/Schema";
import { DEFAULT_INFLATION_RATE, INTEREST_RATE_AGGRESIVE, INTEREST_RATE_CONSERVATIVE } from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { addMonths, differenceInMonths, format, isBefore, startOfDay, startOfMonth } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ModelingAccordion } from "./ModelingAccordion";
import { SavingsChart } from "./SavingsChart";

type Props = {
  readonly onClose: () => void;
  readonly editGoal?: VehicleGoalDto;
  readonly goalId?: string;
};

export const VehicleGoalForm = forwardRef<{}, Props>(({ onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const methods = useForm<VehicleGoalFormType>({
    resolver: yupResolver(getVehicleGoalFormSchema(t)),
    mode: "onBlur",
    defaultValues: editGoal
      ? {
          name: editGoal.name,
          purchaseDate: new Date(editGoal.data.targetDate as string),
          expectedPrice: Number(editGoal.data.vehicleAmount?.value),
          interestRate: Number(editGoal.modeling.appreciationRate),
          includeInflation: editGoal.modeling.inflation?.enabled,
          inflationRate: Number(editGoal.modeling.inflation?.rate),
          includeFee: editGoal.modeling.fee?.enabled,
          feeRate: Number(editGoal.modeling.fee?.rate),
          note: editGoal.note || "",
          priority: editGoal.priorityLevel === VehicleGoalDtoPriorityLevel.high,
          referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
        }
      : {
          includeInflation: true,
          includeFee: true,
          inflationRate: DEFAULT_INFLATION_RATE.toString() as any,
          feeRate: DEFAULT_INFLATION_RATE.toString() as any,
          name: t(Resources.AnalysisDetail.Goals.GoalType.Vehicle),
          referenceStartDate: new Date(),
        },
  });

  const {
    control,
    setValue,
    watch,
    formState: { errors },
    clearErrors,
    handleSubmit,
  } = methods;

  const targetDate = watch("purchaseDate");
  const targetAmount = watch("expectedPrice");
  const interestRate = watch("interestRate");
  const includeInflation = watch("includeInflation");
  const includeFee = watch("includeFee");
  const feeRate = watch("feeRate");
  const inflationRate = watch("inflationRate");
  const monthlyContribution = watch("monthlyContribution");
  const monthlyContributionWithFee = watch("monthlyContributionWithFee");
  const referenceStartDate = watch("referenceStartDate");

  const investmentLength = differenceInMonths(targetDate, new Date());
  const isInvalidPurchaseDate = isBefore(startOfDay(targetDate), startOfDay(minDate));
  const shouldRenderChart =
    targetDate && !isInvalidPurchaseDate && interestRate >= 0 && inflationRate >= 0 && monthlyContribution && targetAmount > 0;

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingMonthlyContributionWithFee, setPendingMonthlyContributionWithFee] = useState<number | null>(null);
  const [lastAutoMonthlyContributionWithFee, setLastAutoMonthlyContributionWithFee] = useState<number | null>(null);

  useEffect(() => {
    if (editGoal) {
      return;
    }

    if (investmentLength && investmentLength > 120) {
      setValue("interestRate", INTEREST_RATE_AGGRESIVE);
    } else {
      setValue("interestRate", INTEREST_RATE_CONSERVATIVE);
    }
  }, [setValue, investmentLength, editGoal]);

  useEffect(() => {
    const durationInMonths = targetDate ? differenceInMonths(targetDate, new Date()) : 0;
    if (includeInflation && inflationRate && interestRate && targetAmount && durationInMonths >= 0) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount,
        durationInMonths,
        interestRate,
        inflationRate: includeInflation ? Number(inflationRate) : 0,
      });
      setValue("monthlyContribution", monthlyContribution.toString() as any);
    }
  }, [includeInflation, inflationRate, interestRate, targetAmount, setValue, targetDate]);

  useEffect(() => {
    if (!targetDate || isInvalidPurchaseDate) return;
    const durationInMonths = targetDate ? differenceInMonths(targetDate, new Date()) : 0;
    const monthlyContribution = calculateMonthlyContribution({
      targetAmount,
      durationInMonths,
      interestRate,
      inflationRate: includeInflation ? Number(inflationRate) : 0,
    });
    const newContributionWithFee = calculateMonthlyContributionWithFee(Number(monthlyContribution), Number(includeFee ? feeRate : 0));
    const roundedWithFee = Math.round(newContributionWithFee);
    setValue("monthlyContributionWithFee", roundedWithFee);
    setLastAutoMonthlyContributionWithFee(roundedWithFee);
  }, [feeRate, inflationRate, interestRate, targetAmount, targetDate, isInvalidPurchaseDate, includeInflation, includeFee, setValue]);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (editGoal) {
        handleSubmit(onEditGoal)();
      } else {
        handleSubmit(onCreateGoal)();
      }
    },
  }));

  useEffect(() => {
    if (!targetDate || isInvalidPurchaseDate) return;
    clearErrors("purchaseDate");
    onChangePurchaseDate();
  }, [targetDate, isInvalidPurchaseDate, clearErrors]);

  const onChangePurchaseDate = () => {
    onChange({
      durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
      targetAmount,
      inflationRate,
      interestRate,
    });
  };

  const queryClient = useQueryClient();

  const { mutate: createVehicleGoal } = useCreateVehicleGoal();

  const { mutate: updateVehicleGoal } = useUpdateVehicleGoal();

  const onChange = ({ inflationRate, interestRate, targetAmount, durationInMonths }: CalculateMonthlyContributionParams) => {
    if (interestRate && targetAmount && durationInMonths >= 0) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount,
        durationInMonths,
        interestRate,
        inflationRate,
      });
      setValue("monthlyContribution", monthlyContribution.toString() as any);
    }
  };

  const onCreateGoal = (data: VehicleGoalFormType) => {
    createVehicleGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? VehicleGoalDtoPriorityLevel.high : VehicleGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.purchaseDate, "yyyy-MM-dd"),
            vehicleAmount: {
              value: data.expectedPrice,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            inflation: {
              enabled: !!includeInflation,
              rate: data.inflationRate,
            },
            investmentAmount: {
              value: includeFee ? data.monthlyContributionWithFee : data.monthlyContribution,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            fee: {
              enabled: !!includeFee,
              rate: data.feeRate,
            },
            appreciationRate: data.interestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEditGoal = (data: VehicleGoalFormType) => {
    updateVehicleGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? VehicleGoalDtoPriorityLevel.high : VehicleGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.purchaseDate, "yyyy-MM-dd"),
            vehicleAmount: {
              value: data.expectedPrice,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            inflation: {
              enabled: !!includeInflation,
              rate: data.inflationRate,
            },
            investmentAmount: {
              value: includeFee ? data.monthlyContributionWithFee : data.monthlyContribution,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            fee: {
              enabled: !!includeFee,
              rate: data.feeRate,
            },
            appreciationRate: data.interestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetVehicleGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const recalculateMonthlyContributionFromTarget = () => {
    const durationInMonths = targetDate ? differenceInMonths(targetDate, new Date()) : 0;
    const baseMonthlyContribution = calculateMonthlyContribution({
      targetAmount,
      durationInMonths,
      interestRate,
      inflationRate: includeInflation ? Number(inflationRate) : 0,
    });
    const withFee = calculateMonthlyContributionWithFee(Number(baseMonthlyContribution), Number(includeFee ? feeRate : 0));
    setValue("monthlyContribution", baseMonthlyContribution.toString() as any);
    const roundedWithFee = Math.round(withFee);
    setValue("monthlyContributionWithFee", roundedWithFee);
    setLastAutoMonthlyContributionWithFee(roundedWithFee);
  };

  const handleConfirmResult = (shouldRecalculateTarget: boolean) => {
    if (shouldRecalculateTarget && pendingMonthlyContributionWithFee != null) {
      const durationInMonths = targetDate ? differenceInMonths(targetDate, new Date()) : 0;
      const monthsToYears = durationInMonths / 12;
      const contributionWithoutFee = calculateMonthlyContributionWithoutFee(
        Number(pendingMonthlyContributionWithFee),
        Number(includeFee ? feeRate : 0),
      );

      const newTarget = calculateTargetAmount(
        Number(contributionWithoutFee),
        monthsToYears,
        Number(interestRate) || 0,
        includeInflation ? Number(inflationRate) || 0 : 0,
      );

      setValue("expectedPrice", Math.round(newTarget) as any);
      setValue("monthlyContribution", contributionWithoutFee.toString() as any);
      const roundedWithFee = Math.round(Number(pendingMonthlyContributionWithFee));
      setValue("monthlyContributionWithFee", roundedWithFee as any);
      setLastAutoMonthlyContributionWithFee(roundedWithFee);
    } else {
      recalculateMonthlyContributionFromTarget();
    }

    setIsConfirmOpen(false);
    setPendingMonthlyContributionWithFee(null);
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <ConfirmRecalculateTargetModal isOpen={isConfirmOpen} handleResult={handleConfirmResult} />
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Vehicle)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <BlCheckboxInput
                  control={control}
                  disabled={readOnly}
                  name="priority"
                  text={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  errors={errors}
                />
              </Grid>
              <Grid size={12}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                />
              </Grid>
              <Grid size={6}>
                <DatePicker
                  control={control}
                  disabled={readOnly}
                  name="purchaseDate"
                  label={t(Resources.AnalysisDetail.Goals.PurchaseDate)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  minDate={startOfMonth(addMonths(new Date(), 1))}
                  errors={errors}
                />
              </Grid>
              <Grid size={6}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="expectedPrice"
                  label={t(Resources.AnalysisDetail.Goals.VehicleValue)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  onBlur={() =>
                    onChange({
                      durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                      targetAmount,
                      inflationRate,
                      interestRate,
                    })
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 0,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
          {targetDate && targetAmount > 0 && (
            <Box>
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 2,
                          radix: ",",
                          mapToRadix: ["."],
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}
                        </Typography>
                      }
                      onBlur={() => {
                        const currentValue = Math.round(Number(monthlyContributionWithFee));
                        if (lastAutoMonthlyContributionWithFee !== null && currentValue === Number(lastAutoMonthlyContributionWithFee)) {
                          return;
                        }
                        setPendingMonthlyContributionWithFee(currentValue);
                        setIsConfirmOpen(true);
                      }}
                    />
                  </Grid>
                  <Grid size={6}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="interestRate"
                      label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 2,
                        },
                      ]}
                      onChange={(newInterestRate) => {
                        if (Number(newInterestRate) === Number(inflationRate)) {
                          setValue("interestRate", interestRate);
                          return;
                        }

                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate,
                          interestRate: newInterestRate as any,
                        });
                      }}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      disabled={readOnly}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      onChange={(checked) => {
                        const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        if (newInflationRate === Number(interestRate)) {
                          setValue("inflationRate", newInflationRate + 0.1);
                        } else {
                          setValue("inflationRate", newInflationRate);
                        }

                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate: newInflationRate,
                          interestRate,
                        });
                      }}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeInflation || readOnly}
                      control={control}
                      name="inflationRate"
                      errors={errors}
                      onChange={(newInflationRate) => {
                        if (Number(newInflationRate) === Number(interestRate)) {
                          setValue("inflationRate", inflationRate);
                          return;
                        }

                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate: includeInflation ? Number(newInflationRate) : 0,
                          interestRate,
                        });
                      }}
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          radix: ",",
                          mapToRadix: ["."],
                          min: 0,
                          max: 20,
                          scale: 1,
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      disabled={readOnly}
                      name="includeFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeFee)}
                      errors={errors}
                      onChange={(checked) => {
                        const newFeeRate = checked ? DEFAULT_INFLATION_RATE : 0;
                        setValue("feeRate", newFeeRate);
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={!includeFee || readOnly}
                      control={control}
                      name="feeRate"
                      errors={errors}
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          scale: 1,
                          radix: ",",
                          mapToRadix: ["."],
                          max: 20,
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </ModelingAccordion>
            </Box>
          )}
          <Grid container spacing={Spacing.XL}>
            {shouldRenderChart && (
              <Grid size={12}>
                <Stack spacing={Spacing.XS} padding={Spacing.S} display="flex" justifyContent="center" alignItems="center">
                  <Typography variant="caption" fontWeight={500}>
                    {t(Resources.Common.Investment)}
                  </Typography>
                  <Typography variant="caption">
                    {formatCurrency(monthlyContributionWithFee, CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                  </Typography>
                </Stack>
                <SavingsChart
                  referenceStartDate={referenceStartDate}
                  targetDate={targetDate}
                  targetAmount={targetAmount}
                  interestRate={interestRate ? Number(interestRate) : 0}
                  monthlyContributionAmount={Number(monthlyContribution)}
                  inflationRate={inflationRate}
                />
              </Grid>
            )}
            <Grid size={12}>
              <BlFormInput
                disabled={readOnly}
                control={control}
                label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                name="note"
                errors={errors}
                multiline
                minRows={6}
                maxRows={8}
                characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
              />
            </Grid>
          </Grid>
        </Stack>
      </form>
    </FormProvider>
  );
});
